import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';

const SalesBill = () => {
  const [sales, setSales] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [formData, setFormData] = useState({
    date: '',
    customer: '',
    items: [{ name: '', quantity: '', price: '', manufacturer: '' }],
    total: ''
  });

  useEffect(() => {
    // 从localStorage加载出货账单数据
    const savedSales = localStorage.getItem('sales');
    if (savedSales) {
      setSales(JSON.parse(savedSales));
    }
  }, []);

  const saveSales = (updatedSales) => {
    setSales(updatedSales);
    localStorage.setItem('sales', JSON.stringify(updatedSales));
  };

  const handleInputChange = (e, index = null) => {
    const { name, value } = e.target;
    if (index !== null) {
      // 处理物品列表的变化
      const updatedItems = [...formData.items];
      updatedItems[index][name] = value;
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const calculateTotal = () => {
    const total = formData.items.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      return sum + (quantity * price);
    }, 0);
    setFormData(prev => ({
      ...prev,
      total: total.toFixed(2)
    }));
  };

  useEffect(() => {
    calculateTotal();
  }, [formData.items]);

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { name: '', quantity: '', price: '', manufacturer: '' }]
    }));
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const handleAddSale = (e) => {
    e.preventDefault();
    const newSale = {
      id: Date.now().toString(),
      ...formData,
      total: parseFloat(formData.total)
    };
    const updatedSales = [...sales, newSale];
    saveSales(updatedSales);
    setFormData({ date: '', customer: '', items: [{ name: '', quantity: '', price: '', manufacturer: '' }], total: '' });
    setShowAddForm(false);
  };

  const handleEditSale = (sale) => {
    setEditingSale(sale.id);
    // 确保每个物品都有manufacturer字段
    const itemsWithManufacturer = sale.items.map(item => ({
      ...item,
      manufacturer: item.manufacturer || ''
    }));
    setFormData({
      date: sale.date,
      customer: sale.customer,
      items: itemsWithManufacturer,
      total: sale.total.toString()
    });
  };

  const handleUpdateSale = (e) => {
    e.preventDefault();
    const updatedSales = sales.map(sale => 
      sale.id === editingSale 
        ? { ...sale, ...formData, total: parseFloat(formData.total) }
        : sale
    );
    saveSales(updatedSales);
    setFormData({ date: '', customer: '', items: [{ name: '', quantity: '', price: '', manufacturer: '' }], total: '' });
    setEditingSale(null);
  };

  const handleDeleteSale = (id) => {
    if (window.confirm('确定要删除这个出货账单吗？')) {
      const updatedSales = sales.filter(sale => sale.id !== id);
      saveSales(updatedSales);
    }
  };

  // 打印出货账单功能
  const handlePrintSale = (sale) => {
    // 创建物品列表HTML
    const itemsHtml = sale.items.map((item, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${item.name}</td>
        <td>${item.quantity}</td>
        <td>${item.price}</td>
        <td>${item.manufacturer || '-'}</td>
        <td>${(parseFloat(item.quantity) * parseFloat(item.price)).toFixed(2)}</td>
      </tr>
    `).join('');

    // 创建打印内容
    const printContent = `
      <div class="print-bill">
        <div class="bill-header">
          <h2>出货单</h2>
          <div class="bill-info">
            <div class="info-row">
              <span class="info-label">日期：</span>
              <span class="info-value">${sale.date}</span>
            </div>
            <div class="info-row">
              <span class="info-label">客户：</span>
              <span class="info-value">${sale.customer}</span>
            </div>
          </div>
        </div>
        <div class="bill-section">
          <h3>物品清单</h3>
          <table class="items-table">
            <thead>
              <tr>
                <th>序号</th>
                <th>物品名称</th>
                <th>数量</th>
                <th>单价</th>
                <th>厂家</th>
                <th>金额</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="5" class="total-label">总计：</td>
                <td class="total-amount">${sale.total}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div class="bill-footer">
          <div class="footer-row">
            <span class="footer-label">制单人：</span>
            <span class="footer-value"></span>
          </div>
          <div class="footer-row">
            <span class="footer-label">审核人：</span>
            <span class="footer-value"></span>
          </div>
          <div class="footer-row">
            <span class="footer-label">客户签字：</span>
            <span class="footer-value"></span>
          </div>
        </div>
      </div>
    `;

    // 创建打印窗口
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>出货单打印</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
            }
            .print-bill {
              width: 100%;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
              border: 2px solid #000;
              box-sizing: border-box;
            }
            .bill-header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 2px solid #000;
            }
            .bill-header h2 {
              margin: 0;
              font-size: 24px;
              font-weight: bold;
            }
            .bill-info {
              margin-top: 15px;
              text-align: left;
            }
            .info-row {
              margin-bottom: 8px;
            }
            .info-label {
              display: inline-block;
              width: 80px;
              font-weight: bold;
            }
            .bill-section {
              margin-bottom: 20px;
            }
            .bill-section h3 {
              margin: 0 0 10px 0;
              font-size: 16px;
              font-weight: bold;
              border-bottom: 1px solid #000;
              padding-bottom: 5px;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
            }
            .items-table th,
            .items-table td {
              border: 1px solid #000;
              padding: 8px;
              text-align: center;
            }
            .items-table th {
              background-color: #f0f0f0;
              font-weight: bold;
            }
            .total-label {
              font-weight: bold;
              text-align: right;
            }
            .total-amount {
              font-weight: bold;
            }
            .bill-footer {
              margin-top: 30px;
              padding-top: 15px;
              border-top: 2px solid #000;
              text-align: left;
            }
            .footer-row {
              margin-bottom: 10px;
            }
            .footer-label {
              display: inline-block;
              width: 100px;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // 导出 Excel 功能
  const handleExportExcel = () => {
    // 准备导出数据
    const exportData = [];
    
    sales.forEach(sale => {
      sale.items.forEach((item, index) => {
        exportData.push({
          日期: sale.date,
          客户: sale.customer,
          物品名称: item.name,
          数量: item.quantity,
          单价: item.price,
          厂家: item.manufacturer || '-',
          金额: (parseFloat(item.quantity) * parseFloat(item.price)).toFixed(2),
          总计: index === 0 ? sale.total : ''
        });
      });
    });

    // 创建工作簿
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '出货账单数据');

    // 导出文件
    XLSX.writeFile(workbook, `出货账单_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // 排序函数
  const sortedSales = [...sales].sort((a, b) => {
    if (sortField === 'date') {
      return sortOrder === 'asc' ? new Date(a.date) - new Date(b.date) : new Date(b.date) - new Date(a.date);
    } else if (sortField === 'total') {
      return sortOrder === 'asc' ? a.total - b.total : b.total - a.total;
    }
    return 0;
  });

  // 处理排序字段和顺序变化
  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="sales-bill">
      <h2>出货账单</h2>
      <button className="add-button" onClick={() => setShowAddForm(!showAddForm)}>
        {showAddForm ? '取消' : '添加出货账单'}
      </button>

      {(showAddForm || editingSale) && (
        <form className="bill-form" onSubmit={editingSale ? handleUpdateSale : handleAddSale}>
          <div className="form-group">
            <label>日期</label>
            <input type="date" name="date" value={formData.date} onChange={handleInputChange} required />
          </div>
          <div className="form-group">
            <label>客户</label>
            <input type="text" name="customer" value={formData.customer} onChange={handleInputChange} required />
          </div>
          
          <h4>物品列表</h4>
          {formData.items.map((item, index) => (
            <div key={index} className="item-row">
              <div className="form-group">
                <label>物品名称</label>
                <input type="text" name="name" value={item.name} onChange={(e) => handleInputChange(e, index)} required />
              </div>
              <div className="form-group">
                <label>数量</label>
                <input type="number" name="quantity" value={item.quantity} onChange={(e) => handleInputChange(e, index)} step="0.01" required />
              </div>
              <div className="form-group">
                <label>单价</label>
                <input type="number" name="price" value={item.price} onChange={(e) => handleInputChange(e, index)} step="0.01" required />
              </div>
              <div className="form-group">
                <label>厂家</label>
                <input type="text" name="manufacturer" value={item.manufacturer} onChange={(e) => handleInputChange(e, index)} required />
              </div>
              <button type="button" className="remove-button" onClick={() => removeItem(index)}>删除</button>
            </div>
          ))}
          <button type="button" className="add-item-button" onClick={addItem}>添加物品</button>

          <div className="form-group total">
            <label>总计</label>
            <input type="number" name="total" value={formData.total} readOnly />
          </div>
          <button type="submit">{editingSale ? '更新' : '保存'}</button>
          {editingSale && <button type="button" onClick={() => setEditingSale(null)}>取消</button>}
        </form>
      )}

      <div className="sales-list">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3>出货账单列表</h3>
          <button className="export-button" onClick={handleExportExcel}>
            导出 Excel
          </button>
        </div>
        <div className="sort-controls">
          <label>排序方式：</label>
          <button 
            className={`sort-button ${sortField === 'date' ? 'active' : ''}`}
            onClick={() => handleSortChange('date')}
          >
            日期 {sortField === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button 
            className={`sort-button ${sortField === 'total' ? 'active' : ''}`}
            onClick={() => handleSortChange('total')}
          >
            金额 {sortField === 'total' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
        </div>
        {sales.length === 0 ? (
          <p>暂无出货账单</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>日期</th>
                <th>客户</th>
                <th>物品数量</th>
                <th>平均单价</th>
                <th>总计</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {sortedSales.map(sale => {
                // 计算平均单价
                const totalItems = sale.items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
                const averagePrice = totalItems > 0 ? (sale.total / totalItems).toFixed(2) : '0.00';
                
                return (
                  <tr key={sale.id}>
                    <td>{sale.date}</td>
                    <td>{sale.customer}</td>
                    <td>{sale.items.length}</td>
                    <td>{averagePrice}</td>
                    <td>{sale.total}</td>
                    <td>
                      <button className="edit-button" onClick={() => handleEditSale(sale)}>编辑</button>
                      <button className="delete-button" onClick={() => handleDeleteSale(sale.id)}>删除</button>
                      <button className="print-button" onClick={() => handlePrintSale(sale)}>打印</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SalesBill;